import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import React, { useEffect, useState } from "react";
import { Button, Form, Input, message, Radio, Select } from "antd";
import { useSelector } from "react-redux";
import { UserSearchReq, getFundingEvaluationPlansRequest, sendBrokerInboxMessage } from "../../utils/api/apis";
import { useNavigate } from "react-router-dom";
import "./BrokerNotification.scss";

const { TextArea } = Input;

/* -------------------- CKEditor Config -------------------- */
const CKEditorInput = ({ value, onChange }) => (
  <div className="ckeditor-wrapper">
    <CKEditor
      editor={ClassicEditor}
      data={value || ""}
      onChange={(event, editor) => {
        const data = editor.getData();
        onChange(
          data === "<p>&nbsp;</p>" ||
            data === '<p><br data-cke-filler="true"></p>' ||
            data === "<p><br></p>" ||
            data === ""
            ? ""
            : data,
        );
      }}
    />
  </div>
);

const BrokerNotification = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [planOptions, setPlanOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [contentType, setContentType] = useState("text");

  const idToken = useSelector((state) => state.auth.idToken);
  const { selectedBrand } = useSelector((state) => state.brand);
  const navigate = useNavigate();

  // Watch for field values to handle hiding logic
  const selectedUserIds = Form.useWatch("user_ids", form);
  const selectedPlanId = Form.useWatch("raw_plan_id", form);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await getFundingEvaluationPlansRequest(idToken, selectedBrand);
        if (response && Array.isArray(response)) {
          setPlanOptions(
            response.map((plan) => ({
              label: plan.name || plan.plan_name,
              value: plan.id,
            })),
          );
        }
      } catch (err) {
        console.error("Error fetching plans:", err);
      }
    };
    if (idToken) {
      fetchPlans();
    }
  }, [idToken, selectedBrand]);

  let timeoutId = null;
  const handleUserSearch = (value) => {
    if (value) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        setIsSearching(true);
        try {
          const res = await UserSearchReq(idToken, value);
          if (res?.status < 399) {
            setUserOptions(
              res.data.map((u) => ({ label: u.email, value: u.id })),
            );
          }
        } catch (err) {
          console.error(err);
        } finally {
          setIsSearching(false);
        }
      }, 600);
    } else {
      setUserOptions([]);
    }
  };

  const handleContentTypeChange = (e) => {
    setContentType(e.target.value);
    form.setFieldsValue({ content: "" });
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        subject: values.subject,
        content: values.content,
        content_type: values.contentType,
        user_ids: values.user_ids && values.user_ids.length > 0 ? values.user_ids : [],
        raw_plan_id: values.raw_plan_id || null,
      };

      const res = await sendBrokerInboxMessage(idToken, payload);
      if (res.status < 399) {
        message.success("Broker notification sent successfully!");
        form.resetFields();
        setContentType("text");
      } else {
        throw new Error(res.data?.message || res.data?.error || "Failed to send notification");
      }
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Logic for hiding fields
  const showUserFields = !selectedPlanId;
  const showPlanFields = !(selectedUserIds && selectedUserIds.length > 0);

  return (
    <div className="inbox-container broker-notification">
      <div className="header-actions">
        <h1>Send Broker Notification</h1>
        <Button onClick={() => navigate(-1)}>Back</Button>
      </div>

      <div className="form-wrapper">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ contentType: "text" }}
        >
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: "Please enter a subject" }]}
          >
            <Input size="large" placeholder="Enter notification subject" />
          </Form.Item>

          {showUserFields && (
            <Form.Item
              name="user_ids"
              label="Target Users"
              extra="Search users by email"
            >
              <Select
                mode="multiple"
                size="large"
                placeholder="Search by email"
                onSearch={handleUserSearch}
                options={userOptions}
                loading={isSearching}
                filterOption={false}
                style={{ width: "100%" }}
                allowClear
              />
            </Form.Item>
          )}

          {showPlanFields && (
            <Form.Item
              name="raw_plan_id"
              label="Target Plan"
            >
              <Select
                size="large"
                placeholder="Select a plan"
                options={planOptions}
                allowClear
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}

          <Form.Item label="Content Type" name="contentType">
            <Radio.Group onChange={handleContentTypeChange}>
              <Radio.Button value="text">Plain Text</Radio.Button>
              <Radio.Button value="html">HTML Layout</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="content"
            label="Notification Body"
            rules={[{ required: true, message: "Please enter message content" }]}
          >
            {contentType === "text" ? (
              <TextArea rows={10} placeholder="Type your message" />
            ) : (
              <CKEditorInput key="html-editor" />
            )}
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block
              className="submit-btn"
            >
              Send Notification
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default BrokerNotification;
